/**
 * Reference lens geometry for the marketing pages: a pinhole model on a 1/3"
 * sensor. The /lens/:mm guides and the coverage table on
 * /camera-placement-tool both read it, so the two never quote different
 * angles for the same focal length. The planner itself uses each camera
 * model's own sensor and lens data, not these constants.
 */

/** Width of a 1/3" sensor, in millimetres. */
export const REFERENCE_SENSOR_WIDTH_MM = 4.8;

/** Horizontal pixels of a 4 MP (2560 × 1440) image. */
export const REFERENCE_4MP_WIDTH_PX = 2560;

/** EN 62676-4 pixel-density thresholds, pixels per metre on the target. */
export const DORI_PX_PER_M = { detect: 25, observe: 62.5, recognize: 125, identify: 250 } as const;

/** Horizontal angle of view in degrees. */
export const horizontalFovDeg = (focalMm: number, sensorWidthMm = REFERENCE_SENSOR_WIDTH_MM): number =>
  2 * Math.atan(sensorWidthMm / (2 * focalMm)) * (180 / Math.PI);

/** Width of the view, in metres, at `distanceM` from the camera. */
export const coverageWidthM = (focalMm: number, distanceM: number, sensorWidthMm = REFERENCE_SENSOR_WIDTH_MM): number =>
  (distanceM * sensorWidthMm) / focalMm;

/** Farthest distance, in metres, at which the image still has `pxPerM` pixels per metre. */
export const doriDistanceM = (
  focalMm: number,
  pxPerM: number,
  widthPx = REFERENCE_4MP_WIDTH_PX,
  sensorWidthMm = REFERENCE_SENSOR_WIDTH_MM,
): number => (widthPx * focalMm) / (pxPerM * sensorWidthMm);
